import { Quaternion, Vector3 } from 'three'
import { BoxSizeEnum, BoxTypeEnum, ColorType, LevelDifficulty, SplineType } from './enums'

/**
 * The shipped `LevelData` (TypeDefIndex 8495), verbatim as it appears in
 * `Resources/Levels/Level_N`. Field names and nesting match the JSON exactly —
 * do not rename; `loadLevel` converts to the runtime shape below.
 */
export interface RawVector3 {
  x: number
  y: number
  z: number
}
export interface RawQuaternion {
  x: number
  y: number
  z: number
  w: number
}

export interface RawNodeData {
  nodePosition: RawVector3
  tangent1: RawVector3
  tangent2: RawVector3
}

export interface RawBoxData {
  boxPosition: RawVector3
  boxRotation: RawQuaternion
  boxColor: number
  boxSize: number
  boxType: number
  /** Absent in the JSON when 0. */
  iceBreakCount?: number
}

export interface RawCameraData {
  cameraPosition: RawVector3
  cameraRotation: RawQuaternion
  cameraOrthographicSize: number
}

export interface RawElevatorData {
  elevatorPosition: RawVector3
  elevatorRotation: RawQuaternion
  /**
   * Box positions are local to **their own layer**, not to the elevator.
   *
   * `GenerateBoxFromElevator` calls `layer.transform.TransformPoint(...)`, and
   * `GenerateLayers` parks layer `i` at elevator-local `(0, -8i, 0)`. Every box
   * in every layer of all 40 levels stores `y = 0`, which is the proof — if
   * they were elevator-local, layer 1 would have to carry the -8 itself. The
   * two readings coincide on layer 0, which is why calling them elevator-local
   * (as this comment used to) went unnoticed until T14a.
   */
  layerData: { boxes: RawBoxData[] }[]
}

export interface RawQueueData {
  queuePosition: RawVector3
  queueRotation: RawQuaternion
  mirrored: boolean
  people: number[]
}

export interface RawWaitingAreaData {
  areaPosition: RawVector3
  areaRotation: RawQuaternion
}

export interface RawLevelData {
  SplineReversed: boolean
  LevelNumber: number
  index: number
  difficulty: number
  SplineIsClosed: boolean
  Nodes: RawNodeData[]
  SplineType: number
  Boxes: RawBoxData[]
  CameraData: RawCameraData
  ElevatorData: RawElevatorData[]
  QueueData: RawQueueData[]
  WaitingAreas: RawWaitingAreaData[]
  /** Only present when `HasConveyorIndicatorPosition` was true. */
  ConveyorIndicatorPosition?: RawVector3
}

// ---------------------------------------------------------------------------
// Runtime shape
// ---------------------------------------------------------------------------

export interface NodeSpec {
  position: Vector3
  tangent1: Vector3
  tangent2: Vector3
}

export interface BoxSpec {
  position: Vector3
  rotation: Quaternion
  color: ColorType
  size: BoxSizeEnum
  type: BoxTypeEnum
  iceBreakCount: number
}

export interface CameraSpec {
  position: Vector3
  rotation: Quaternion
  orthographicSize: number
}

export interface ElevatorSpec {
  position: Vector3
  rotation: Quaternion
  /**
   * Outer array = layers. Index 0 is the one standing at lot height; the rest
   * wait **below** it, 8 units apart, and rise as each is emptied.
   */
  layers: BoxSpec[][]
}

export interface QueueSpec {
  position: Vector3
  rotation: Quaternion
  mirrored: boolean
  people: ColorType[]
}

export interface WaitingAreaSpec {
  position: Vector3
  rotation: Quaternion
}

export interface Level {
  number: number
  index: number
  difficulty: LevelDifficulty
  splineReversed: boolean
  splineIsClosed: boolean
  splineType: SplineType
  nodes: NodeSpec[]
  boxes: BoxSpec[]
  camera: CameraSpec
  elevators: ElevatorSpec[]
  queues: QueueSpec[]
  waitingAreas: WaitingAreaSpec[]
  conveyorIndicatorPosition: Vector3 | null
  raw: RawLevelData
}

const v3 = (v: RawVector3) => new Vector3(v.x, v.y, v.z)
const quat = (q: RawQuaternion) => new Quaternion(q.x, q.y, q.z, q.w)

function box(b: RawBoxData): BoxSpec {
  return {
    position: v3(b.boxPosition),
    rotation: quat(b.boxRotation),
    color: b.boxColor as ColorType,
    size: b.boxSize as BoxSizeEnum,
    type: b.boxType as BoxTypeEnum,
    iceBreakCount: b.iceBreakCount ?? 0,
  }
}

export function parseLevel(raw: RawLevelData): Level {
  return {
    number: raw.LevelNumber,
    index: raw.index,
    difficulty: raw.difficulty as LevelDifficulty,
    splineReversed: raw.SplineReversed,
    splineIsClosed: raw.SplineIsClosed,
    splineType: raw.SplineType as SplineType,
    nodes: raw.Nodes.map((n) => ({
      position: v3(n.nodePosition),
      tangent1: v3(n.tangent1),
      tangent2: v3(n.tangent2),
    })),
    boxes: raw.Boxes.map(box),
    camera: {
      position: v3(raw.CameraData.cameraPosition),
      rotation: quat(raw.CameraData.cameraRotation),
      orthographicSize: raw.CameraData.cameraOrthographicSize,
    },
    elevators: raw.ElevatorData.map((e) => ({
      position: v3(e.elevatorPosition),
      rotation: quat(e.elevatorRotation),
      layers: e.layerData.map((l) => l.boxes.map(box)),
    })),
    queues: raw.QueueData.map((q) => ({
      position: v3(q.queuePosition),
      rotation: quat(q.queueRotation),
      mirrored: q.mirrored,
      people: q.people as ColorType[],
    })),
    waitingAreas: raw.WaitingAreas.map((w) => ({
      position: v3(w.areaPosition),
      rotation: quat(w.areaRotation),
    })),
    conveyorIndicatorPosition: raw.ConveyorIndicatorPosition
      ? v3(raw.ConveyorIndicatorPosition)
      : null,
    raw,
  }
}

/**
 * 🔍 `LevelService.LoopStartLevel` — `dump.cs`:706868, `const int = 16`.
 * Past the last file the ladder wraps to here, not to 1.
 */
export const LOOP_START_LEVEL = 16
/**
 * 📐 `_levelFiles.Length`. Not a constant in the original: `LevelService..ctor`
 * (`__0x20d5ccc.c`:38) fills it with `Resources.LoadAll("Levels")`, so the
 * number is whatever shipped. Recovered by counting `web/public/data/levels/`
 * — 40 files, the count `npm run invariants` walks.
 */
export const LEVEL_COUNT = 40

/**
 * 🔍 `LevelService.GetResolvedFileIndex` — `__0x20d7e30.c`:9-27, RVA 0x20d7e30.
 * The same body is inlined into `get_UniqueLevelIndex` (0x20d7ddc), `NextLevel`,
 * `PreviousLevel`, `SkipToLevel` (0x20d838c) and `SuccessModel.LoadLNextLevel`
 * (0x2141cdc), which is why it is worth having once here.
 *
 *     if (d < 2) d = 1;
 *     n = _levelFiles.Length;  if (n == 0) return 1;
 *     if (d <= n) return d;
 *     span = n - 0xf;          if (span <= 0) return 1;
 *     return ((d + ~n) - ((d + ~n) / span) * span) + 0x10;
 *
 * `~n` is `-n - 1`, so the tail is `(d - n - 1) mod span + LOOP_START_LEVEL`.
 * With n = 40: 40 → 40, **41 → 16**, 65 → 40, 66 → 16 — an endless lap of
 * 16..40, exactly what `LoopStartLevel` names.
 *
 * @param displayLevel the ladder step (`LocalSaveData.displayLevel`).
 * @returns a level file number in 1..fileCount.
 */
export function resolvedFileIndex(displayLevel: number, fileCount = LEVEL_COUNT): number {
  const d = displayLevel < 2 ? 1 : displayLevel
  if (fileCount === 0) return 1
  if (d <= fileCount) return d
  const span = fileCount - (LOOP_START_LEVEL - 1)
  if (span <= 0) return 1
  const rest = d - fileCount - 1
  // C's `/` on ints truncates toward zero; `rest` is > 0 on this branch (`d`
  // is past `fileCount`), so `Math.floor` and truncation agree.
  return (rest - Math.floor(rest / span) * span) + LOOP_START_LEVEL
}

/**
 * What `?level=` asks for: a clamped level file number, or `null` for "no
 * request — resume from the save" (WEB-4).
 *
 * This is a port-only entry point (the original has a level-select screen, not
 * a URL), but it is the gate in front of `LocalSave.skipToLevel`, which
 * **writes**, so getting it wrong costs saved progress rather than one page
 * load. It lives here, out of `main.ts`, only so a test can reach it: `main.ts`
 * touches the DOM at module scope and cannot be imported.
 *
 * ⚠️ The trap this exists to close: `URLSearchParams.get` answers `null` for a
 * missing parameter, `Number(null)` is `0` — **not** `NaN` — and
 * `Number.isFinite(0)` is `true`. Branching on the parsed number therefore
 * treats a bare `/` as `?level=0`, clamps it up to 1 and calls `skipToLevel`,
 * silently stamping level 1 over the save on every plain visit. `?level=`
 * with an empty value is the same trap, since `Number('')` is also `0`.
 *
 * @param raw the parameter exactly as `URLSearchParams.get` returned it.
 * @returns a file number in 1..fileCount, or `null` to resume from the save.
 */
export function requestedLevelFrom(raw: string | null, fileCount = LEVEL_COUNT): number | null {
  if (raw === null || raw.trim() === '') return null
  const n = Math.round(Number(raw))
  if (!Number.isFinite(n)) return null
  return Math.min(Math.max(n, 1), fileCount)
}

/**
 * The raw JSON of every level fetched so far.
 *
 * Only the **raw** form is cached, and `parseLevel` still runs on every call: a
 * `Level` is handed to `GameSession`, which builds its buses from it, so two
 * loads of the same level must not share one object. Restarting a level was
 * paying for a fetch and a `JSON.parse` it had already done, right in the middle
 * of a transition where every millisecond is on screen.
 */
const rawLevels = new Map<number, RawLevelData>()

export async function loadLevel(levelNumber: number): Promise<Level> {
  let raw = rawLevels.get(levelNumber)
  if (!raw) {
    const res = await fetch(`data/levels/Level_${levelNumber}.json`)
    if (!res.ok) throw new Error(`Level_${levelNumber}.json → HTTP ${res.status}`)
    raw = (await res.json()) as RawLevelData
    rawLevels.set(levelNumber, raw)
  }
  return parseLevel(raw)
}
